// ============================================
// Batch Analysis Service - Queue-based domain analysis
// ============================================

import { GeminiService } from "./geminiService";
import { BatchAnalysisJob, AIAnalysisResult } from "../types";

// Maximum domains per batch
const MAX_BATCH_SIZE = 50;
// Delay between API calls to avoid rate limiting (ms)
const API_DELAY = 500;
// Maximum concurrent jobs (reserved for future use)
// const MAX_CONCURRENT_JOBS = 1;

type BatchJobCallback = (job: BatchAnalysisJob) => void;

/**
 * BatchAnalysisService
 *
 * Manages batch domain analysis with:
 * - Queue-based processing
 * - Progress tracking
 * - Frontend polling support
 * - Rate limiting
 */
export class BatchAnalysisService {
  private geminiService: GeminiService;
  private jobs: Map<string, BatchAnalysisJob> = new Map();
  private processingQueue: string[] = [];
  private isProcessing = false;
  private subscribers: Map<string, Set<BatchJobCallback>> = new Map();

  constructor(geminiService: GeminiService) {
    this.geminiService = geminiService;
  }

  /**
   * Create a new batch analysis job
   */
  createJob(domains: string[]): BatchAnalysisJob {
    // Validate domains
    const validDomains = domains
      .map((d) => d.trim().toLowerCase())
      .filter((d) => d.length > 0)
      .slice(0, MAX_BATCH_SIZE);

    if (validDomains.length === 0) {
      throw new Error("No valid domains provided");
    }

    const job: BatchAnalysisJob = {
      id: `batch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      domains: validDomains,
      status: "queued",
      progress: 0,
      results: {},
      createdAt: new Date().toISOString(),
      completedAt: null,
      error: null,
    };

    this.jobs.set(job.id, job);
    this.processingQueue.push(job.id);
    this.processQueue();

    return job;
  }

  /**
   * Get job by ID
   */
  getJob(jobId: string): BatchAnalysisJob | null {
    return this.jobs.get(jobId) || null;
  }

  /**
   * Get all jobs
   */
  getAllJobs(): BatchAnalysisJob[] {
    return Array.from(this.jobs.values()).sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }

  /**
   * Cancel a job
   */
  cancelJob(jobId: string): boolean {
    const job = this.jobs.get(jobId);
    if (!job) return false;

    if (job.status === "queued" || job.status === "processing") {
      job.status = "cancelled";
      this.notifySubscribers(jobId, job);
      return true;
    }

    return false;
  }

  /**
   * Delete a job
   */
  deleteJob(jobId: string): boolean {
    const job = this.jobs.get(jobId);
    if (!job) return false;

    // Can only delete completed/cancelled/failed jobs
    if (["completed", "cancelled", "failed"].includes(job.status)) {
      this.jobs.delete(jobId);
      this.subscribers.delete(jobId);
      return true;
    }

    return false;
  }

  /**
   * Subscribe to job updates
   */
  subscribe(jobId: string, callback: BatchJobCallback): () => void {
    if (!this.subscribers.has(jobId)) {
      this.subscribers.set(jobId, new Set());
    }
    this.subscribers.get(jobId)!.add(callback);

    // Return unsubscribe function
    return () => {
      this.subscribers.get(jobId)?.delete(callback);
    };
  }

  /**
   * Poll for job status (for frontend polling)
   */
  pollJobStatus(jobId: string): { job: BatchAnalysisJob | null; done: boolean } {
    const job = this.jobs.get(jobId);
    if (!job) {
      return { job: null, done: true };
    }

    const done = ["completed", "cancelled", "failed"].includes(job.status);
    return { job, done };
  }

  /**
   * Process the job queue
   */
  private async processQueue(): Promise<void> {
    if (this.isProcessing) return;
    if (this.processingQueue.length === 0) return;

    this.isProcessing = true;

    while (this.processingQueue.length > 0) {
      const jobId = this.processingQueue.shift();
      if (!jobId) continue;

      const job = this.jobs.get(jobId);
      if (!job || job.status === "cancelled") continue;

      try {
        await this.processJob(job);
      } catch (error) {
        job.status = "failed";
        job.error = error instanceof Error ? error.message : "Unknown error";
        this.notifySubscribers(jobId, job);
      }
    }

    this.isProcessing = false;
  }

  /**
   * Process a single job
   */
  private async processJob(job: BatchAnalysisJob): Promise<void> {
    job.status = "processing";
    this.notifySubscribers(job.id, job);

    const totalDomains = job.domains.length;

    for (let i = 0; i < totalDomains; i++) {
      // Check if cancelled
      // Re-fetch job status to check for cancellation
      const currentJob = this.jobs.get(job.id);
      if (!currentJob || currentJob.status === "cancelled") {
        return;
      }

      const domain = job.domains[i];

      try {
        // Analyze domain
        const analysis = await this.geminiService.analyzeDomain(domain, 0);
        job.results[domain] = analysis;
      } catch (error) {
        // Store error as partial result
        job.results[domain] = {
          valuation: "Error",
          verdict: "Pass",
          analysis: `Failed to analyze: ${error instanceof Error ? error.message : "Unknown error"}`,
          seo_notes: "",
        };
      }

      // Update progress
      job.progress = Math.round(((i + 1) / totalDomains) * 100);
      this.notifySubscribers(job.id, job);

      // Rate limiting delay (except for last item)
      if (i < totalDomains - 1) {
        await this.delay(API_DELAY);
      }
    }

    // Mark as completed
    job.status = "completed";
    job.completedAt = new Date().toISOString();
    this.notifySubscribers(job.id, job);
  }

  /**
   * Notify subscribers of job update
   */
  private notifySubscribers(jobId: string, job: BatchAnalysisJob): void {
    const callbacks = this.subscribers.get(jobId);
    if (callbacks) {
      callbacks.forEach((cb) => cb(job));
    }
  }

  /**
   * Delay helper
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Get queue stats
   */
  getStats(): {
    totalJobs: number;
    queued: number;
    processing: number;
    completed: number;
    failed: number;
  } {
    const jobs = Array.from(this.jobs.values());
    return {
      totalJobs: jobs.length,
      queued: jobs.filter((j) => j.status === "queued").length,
      processing: jobs.filter((j) => j.status === "processing").length,
      completed: jobs.filter((j) => j.status === "completed").length,
      failed: jobs.filter((j) => j.status === "failed").length,
    };
  }

  /**
   * Export results as CSV
   */
  exportResultsAsCSV(jobId: string): string {
    const job = this.jobs.get(jobId);
    if (!job) return "";

    const headers = ["Domain", "Verdict", "Valuation", "Analysis", "SEO Notes"];
    const rows = job.domains.map((domain) => {
      const result = job.results[domain];
      if (!result) {
        return [domain, "Pending", "", "", ""];
      }
      return [
        domain,
        result.verdict,
        result.valuation,
        `"${result.analysis.replace(/"/g, '""')}"`,
        `"${result.seo_notes.replace(/"/g, '""')}"`,
      ];
    });

    return [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
  }

  /**
   * Get top picks from a completed job
   */
  getTopPicks(jobId: string): { domain: string; analysis: AIAnalysisResult }[] {
    const job = this.jobs.get(jobId);
    if (!job || job.status !== "completed") return [];

    return Object.entries(job.results)
      .filter(([_, result]) => result.verdict === "Top Pick")
      .map(([domain, analysis]) => ({ domain, analysis }));
  }
}

// Singleton instance
let batchServiceInstance: BatchAnalysisService | null = null;

export function getBatchAnalysisService(
  geminiService: GeminiService
): BatchAnalysisService {
  if (!batchServiceInstance) {
    batchServiceInstance = new BatchAnalysisService(geminiService);
  }
  return batchServiceInstance;
}
